import React from 'react';
import { BANDS, BAND_CLASSES, bandFor, formatHrnPill } from '../constants/riskAssessment';

/**
 * Fixed-bottom risk summary — mirrors the BudgetCostCodes sticky totals
 * pattern but at the viewport level (always visible while scrolling). Shows
 * the highest residual HRN across all hazards + per-band counts so the user
 * sees at a glance how close they are to "all green".
 *
 * Used by:
 *   - RiskAssessmentDetail.js (per-RA aggregate across sections.rows)
 *   - RiskAssessmentTemplateEditor.js (per-template aggregate across blocks)
 *
 * Props:
 *   - stats: { count, maxResidual, byBand:{green,yellow,orange,red} }
 *            (use computeOverallStats from utils/raStats.js)
 *   - pushUp: bool — lift ~60px when another save-bar is visible below
 */
export default function OverallRiskFooter({ stats, pushUp = false }) {
  if (!stats || stats.count === 0) return null;
  const band = bandFor(stats.maxResidual);
  const bandTone = band.tone;
  const bottomClass = pushUp ? 'bottom-[60px]' : 'bottom-0';
  return (
    <div
      className={`fixed ${bottomClass} left-0 right-0 sm:left-64 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2.5 z-30`}
      data-testid="ra-overall-risk-footer"
    >
      <div className="max-w-full mx-auto flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Worst residual
          </span>
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${BAND_CLASSES[bandTone]}`}
            title="The highest residual HRN among all hazards — the operative number for sign-off"
          >
            {formatHrnPill(stats.maxResidual)}
          </span>
        </div>

        {/* Mini band strip with count per band */}
        {/* 📱 Below `md` (mobile batch item 14) the strip takes a FULL row
            (`order-2 basis-full`) and the hazard count joins the Worst
            residual line, so all four band names fit untruncated — a long
            name wraps under its count instead of ending in "…". */}
        <div className="flex-1 min-w-[200px] flex items-center gap-2 max-md:order-2 max-md:basis-full max-md:items-start">
          {BANDS.map((b) => {
            const n = stats.byBand[b.tone] || 0;
            const active = n > 0;
            const bgClass = {
              green:  active ? 'bg-green-200  dark:bg-green-900/40'  : 'bg-gray-100 dark:bg-gray-700/40',
              yellow: active ? 'bg-yellow-200 dark:bg-yellow-900/40' : 'bg-gray-100 dark:bg-gray-700/40',
              orange: active ? 'bg-orange-200 dark:bg-orange-900/40' : 'bg-gray-100 dark:bg-gray-700/40',
              red:    active ? 'bg-red-200    dark:bg-red-900/40'    : 'bg-gray-100 dark:bg-gray-700/40',
            }[b.tone];
            const textClass = {
              green:  'text-green-700  dark:text-green-300',
              yellow: 'text-yellow-700 dark:text-yellow-300',
              orange: 'text-orange-700 dark:text-orange-300',
              red:    'text-red-700    dark:text-red-300',
            }[b.tone];
            return (
              <div key={b.key} className="flex-1 min-w-0">
                <div className={`h-2 rounded-sm ${bgClass}`} aria-hidden />
                <div className={`flex items-baseline justify-between mt-1 text-[11px] max-md:gap-1 ${active ? textClass : 'text-gray-400 dark:text-gray-500'}`}>
                  <span className="truncate max-md:whitespace-normal max-md:overflow-visible max-md:leading-tight" data-band-label="">{b.label}</span>
                  <span className="font-semibold tabular-nums">{n}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 shrink-0 max-md:order-1 max-md:ml-auto">
          {stats.count} hazard{stats.count !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
}
